import { isEmpty } from 'lodash';
import {
  PatientSearchFilters,
  QuickFilter,
  SearchFilterType,
  type FilterConfigsMap,
  type SavedSearchFilter,
} from './types';
import { ClinicTO, TeamTO } from '../../../lib';
import Swal from 'sweetalert2';
import {
  mapRelationaOperatorToText,
  mapTextToRelationaOperator,
} from './validator/main';
import { validateAge } from './validator/filtersValidator';

export const checkSingleFlowCompleted = (flow: QuickFilter): boolean => {
  return false;
};

export const checkAllFlowsCompleted = (flows: QuickFilter[]): boolean => {
  return false;
};

export const isPatientSearchFilterKey = (
  value: string,
): value is keyof typeof PatientSearchFilters => {
  return Object.prototype.hasOwnProperty.call(PatientSearchFilters, value);
};

export const getDefaultSearchFilterLabel = (
  filterKey: keyof typeof PatientSearchFilters,
) => {
  return PatientSearchFilters[filterKey];
};

export const getSearchFilterLabel = (
  filterKey: string,
  searchFiltersMap?: Partial<FilterConfigsMap>,
) => {
  if (searchFiltersMap?.[filterKey]?.label) {
    return searchFiltersMap[filterKey].label;
  }
  if (isPatientSearchFilterKey(filterKey)) {
    return getDefaultSearchFilterLabel(filterKey);
  }
  return filterKey;
};

export const getFilterKeyByLabel = (
  label: string,
  searchFiltersMap?: Partial<FilterConfigsMap>,
): string | undefined => {
  const configuredKeys = searchFiltersMap ? Object.keys(searchFiltersMap) : [];
  const knownKeys = Object.keys(PatientSearchFilters);
  const keys = Array.from(new Set([...configuredKeys, ...knownKeys]));

  return keys.find(
    (filterKey) =>
      (isPatientSearchFilterKey(filterKey) &&
        label === getDefaultSearchFilterLabel(filterKey)) ||
      label === getSearchFilterLabel(filterKey, searchFiltersMap),
  );
};

export const getConfiguredSearchFilterType = (
  filterKey: string | undefined,
  label: string,
  searchFiltersMap?: Partial<FilterConfigsMap>,
) => {
  const configuredType = filterKey
    ? searchFiltersMap?.[filterKey]?.type
    : undefined;

  if (configuredType) {
    return configuredType as SearchFilterType;
  }

  if (filterKey && isPatientSearchFilterKey(filterKey)) {
    return getSearchFilterType(getDefaultSearchFilterLabel(filterKey));
  }

  return getSearchFilterType(label as PatientSearchFilters);
};

export const getQuickFilterKey = (
  filter: Pick<QuickFilter, 'filterKey' | 'label'>,
  searchFiltersMap?: Partial<FilterConfigsMap>,
) => {
  return (
    filter.filterKey || getFilterKeyByLabel(filter.label, searchFiltersMap)
  );
};

export const generateFilterOption = (
  label: string,
  id: string,
  options: Partial<Pick<QuickFilter, 'filterKey' | 'type' | 'value'>> = {},
): QuickFilter => {
  const filterKey =
    options.filterKey ||
    (isPatientSearchFilterKey(id) ? id : undefined) ||
    getFilterKeyByLabel(label);
  return {
    id,
    label,
    filterKey,
    type: options.type || getConfiguredSearchFilterType(filterKey, label),
    value: options.value,
  };
};

export const generateExistingData = (
  clinics: ClinicTO[],
  teams: TeamTO[],
): Record<PatientSearchFilters, string[]> | null => {
  const data: Record<PatientSearchFilters, string[]> = {
    [PatientSearchFilters.SpecificPatient]: [],
    [PatientSearchFilters.MyPainHealthSpecificPatient]: [],
    [PatientSearchFilters.MyPainHealthNotInvited]: [],
    [PatientSearchFilters.MyPainHealthNotCompletedOnboarding]: [],
    [PatientSearchFilters.MyPainHealthCompletedOnboarding]: [],
    [PatientSearchFilters.MyPainHealthReadyForReview]: [],
    [PatientSearchFilters.MyPainHealthOpenSafetyAlerts]: [],
    [PatientSearchFilters.DischargeNextDay]: [],
    [PatientSearchFilters.AgeFilter]: ['=', '>=', '<='],
    [PatientSearchFilters.TagsOrReviewTriggers]: [],
    [PatientSearchFilters.SpecificUnit]: [],
    [PatientSearchFilters.AdmissionStatus]: [],
    [PatientSearchFilters.SpecificHospital]: [],
    [PatientSearchFilters.OpenTasks]: [],
    [PatientSearchFilters.TasksDueToday]: [],
    [PatientSearchFilters.AssignedToMe]: [],
    [PatientSearchFilters.AssignedToMyTeam]: [],
    [PatientSearchFilters.ParticularMedicines]: [],
    [PatientSearchFilters.ParticularPathology]: [],
    [PatientSearchFilters.MedicinesAndPathology]: [],
    [PatientSearchFilters.IndigenousStatus]: [],
    [PatientSearchFilters.ParticularDrugAllergy]: [],
    [PatientSearchFilters.ResidentialNursingHome]: [],
  };
  for (const clinic of clinics) {
    if (clinic?.organisation_unit === 'hospital') {
      data[PatientSearchFilters.SpecificHospital] = [
        ...data[PatientSearchFilters.SpecificHospital],
        clinic.clinic,
      ];
    } else if (clinic?.organisation_unit === 'team') {
      data[PatientSearchFilters.AssignedToMyTeam] = [
        ...data[PatientSearchFilters.AssignedToMyTeam],
        clinic.clinic,
      ];
    }
  }
  for (const team of teams) {
    if (team?.organisationUnit === 'team') {
      data[PatientSearchFilters.AssignedToMyTeam] = [
        ...data[PatientSearchFilters.AssignedToMyTeam],
        team.clinicName,
      ];
    }
  }
  data[PatientSearchFilters.AssignedToMyTeam] = Array.from(
    new Set(data[PatientSearchFilters.AssignedToMyTeam].filter(Boolean)),
  );
  if (isEmpty(data)) {
    return null;
  }
  return data;
};

const OPEN_SAFETY_ALERT_FILTER_KEY = 'MyPainHealthOpenSafetyAlerts';
const READY_FOR_REVIEW_FILTER_KEY = 'MyPathReadyForReview';

export const isOpenSafetyAlertsFilterKey = (filterKey?: string) => {
  return filterKey === OPEN_SAFETY_ALERT_FILTER_KEY;
};

export const isReadyForReviewFilterKey = (filterKey?: string) => {
  return filterKey === READY_FOR_REVIEW_FILTER_KEY;
};

export const sanitizeSearchFilterTokens = (
  filters: string[],
  searchFiltersMap?: Partial<FilterConfigsMap>,
  options: {
    allowOpenSafetyAlerts: boolean;
    allowReadyForReview: boolean;
  } = {
    allowOpenSafetyAlerts: true,
    allowReadyForReview: true,
  },
): string[] => {
  if (
    (options.allowOpenSafetyAlerts && options.allowReadyForReview) ||
    filters.length === 0
  ) {
    return filters;
  }

  const sanitizedFilters: string[] = [];

  for (let i = 0; i < filters.length; i++) {
    const token = filters[i];
    const filterKey = getFilterKeyByLabel(token, searchFiltersMap);

    const shouldRemoveOpenSafetyAlerts =
      !options.allowOpenSafetyAlerts && isOpenSafetyAlertsFilterKey(filterKey);
    const shouldRemoveReadyForReview =
      !options.allowReadyForReview && isReadyForReviewFilterKey(filterKey);

    if (!shouldRemoveOpenSafetyAlerts && !shouldRemoveReadyForReview) {
      sanitizedFilters.push(token);
      continue;
    }

    const filterType = getConfiguredSearchFilterType(
      filterKey,
      token,
      searchFiltersMap,
    );
    const nextToken = filters[i + 1];
    const nextIsLabel = nextToken
      ? !!getFilterKeyByLabel(nextToken, searchFiltersMap)
      : false;

    if (
      filterType !== SearchFilterType.NO_SELECTION &&
      nextToken &&
      !nextIsLabel
    ) {
      i++;
    }
  }

  return sanitizedFilters;
};

const isSearchAndMultiSelect = (filter: PatientSearchFilters) => {
  // return (
  //   filter === PatientSearchFilters.ParticularMedicines ||
  //   filter === PatientSearchFilters.TagsOrReviewTriggers ||
  //   filter === PatientSearchFilters.ParticularPathology ||
  //   filter === PatientSearchFilters.MedicinesAndPathology ||
  //   filter === PatientSearchFilters.ParticularDrugAllergy ||
  //   filter === PatientSearchFilters.SpecificUnit
  // );
  return false;
};

export const isDoNothingSelect = (filter: PatientSearchFilters) => {
  return (
    filter === PatientSearchFilters.IndigenousStatus ||
    filter === PatientSearchFilters.ResidentialNursingHome ||
    filter === PatientSearchFilters.AssignedToMe ||
    filter === PatientSearchFilters.DischargeNextDay ||
    filter === PatientSearchFilters.TasksDueToday
  );
};

const isSearchAndSingleSelect = (filter: PatientSearchFilters) => {
  return filter === PatientSearchFilters.SpecificPatient;
};

const isSingleSelect = (filter: PatientSearchFilters) => {
  return (
    filter === PatientSearchFilters.SpecificHospital ||
    filter === PatientSearchFilters.AssignedToMyTeam ||
    filter === PatientSearchFilters.AdmissionStatus
  );
};

const isMultiSelect = (filter: PatientSearchFilters) => {
  return (
    filter === PatientSearchFilters.TagsOrReviewTriggers ||
    filter === PatientSearchFilters.AgeFilter ||
    filter === PatientSearchFilters.ParticularMedicines ||
    filter === PatientSearchFilters.ParticularPathology ||
    filter === PatientSearchFilters.MedicinesAndPathology ||
    filter === PatientSearchFilters.ParticularDrugAllergy ||
    filter === PatientSearchFilters.SpecificUnit
  );
};

export const currentSearchFilterInputNeeded = (
  currentFilter: SearchFilterType,
) => {
  return (
    currentFilter === SearchFilterType.SEARCH_AND_MULTI_SELECT ||
    currentFilter === SearchFilterType.SEARCH_AND_SINGLE_SELECT
  );
};

export const currentSearchFilterRemoteOptionsNeeded = (
  currentFilter: SearchFilterType,
) => {
  return (
    currentFilter === SearchFilterType.SINGLE_SELECTION ||
    currentFilter === SearchFilterType.MULTI_SELECTION
  );
};
export const currentSearchFilterMultipleSelection = (
  currentFilter: SearchFilterType,
) => {
  return (
    currentFilter === SearchFilterType.MULTI_SELECTION ||
    currentFilter === SearchFilterType.SEARCH_AND_MULTI_SELECT
  );
};

export const getSearchFilterType = (filter: PatientSearchFilters) => {
  if (isSearchAndMultiSelect(filter)) {
    return SearchFilterType.SEARCH_AND_MULTI_SELECT;
  }
  if (isSearchAndSingleSelect(filter)) {
    return SearchFilterType.SEARCH_AND_SINGLE_SELECT;
  }
  if (isSingleSelect(filter)) {
    return SearchFilterType.SINGLE_SELECTION;
  }
  if (isMultiSelect(filter)) {
    return SearchFilterType.MULTI_SELECTION;
  }
  return SearchFilterType.NO_SELECTION;
};

export const isNumericString = (str: string): boolean => {
  return /^\d+(\.\d+)?$/.test(str);
};

/**
 * According to the chosen operators, return the remaining valid operators.
 * @param {QuickFilter[]} chosenOps - The chosen operators, which can be ["=", ">=", "<="]
 * @returns {string[]} - The remaining valid operators
 */
export const getRemainingValidOperators = (
  allOptions: QuickFilter[],
  chosen: string[],
) => {
  const allOps = [...allOptions];
  const chosenOps = chosen.filter((op) => !isNumericString(op));
  // if "=" is chosen, then no more operators can be chosen
  if (chosenOps.includes('=')) {
    return [];
  }
  // if no operator is chosen, then all operators can be chosen
  if (chosenOps.length === 0) {
    return allOps;
  }
  // if only one operator is chosen
  if (chosenOps.length === 1) {
    const op = chosenOps[0];
    // if the chosen operator is "=", then no more operators can be chosen
    if (op === '>=') {
      return ['<='].map((filter, index) =>
        generateFilterOption(
          mapRelationaOperatorToText(filter),
          `${PatientSearchFilters.AgeFilter}-${filter}-${index}`,
        ),
      );
    }
    // if the chosen operator is "<" or "<=", then no more operators can be chosen
    if (op === '<=') {
      return ['>='].map((filter, index) =>
        generateFilterOption(
          mapRelationaOperatorToText(filter),
          `${PatientSearchFilters.AgeFilter}-${filter}-${index}`,
        ),
      );
    }
    // if the chosen operator is ">" or ">=", then no more operators can be chosen
    return [];
  }
  // if two operators are chosen
  return [];
};

export function joinFilterTokens(
  arr: string[],
  searchFiltersMap?: Partial<FilterConfigsMap>,
): string[] {
  const result: string[] = [];
  let i = 0;

  const isFilterMarker = (s: string) =>
    !!getFilterKeyByLabel(s, searchFiltersMap);

  while (i < arr.length) {
    if (isFilterMarker(arr[i])) {
      result.push(arr[i]);
      i++;
      const tokens: string[] = [];
      while (i < arr.length && !isFilterMarker(arr[i])) {
        tokens.push(arr[i]);
        i++;
      }
      if (tokens.length > 0) {
        result.push(tokens.join(', '));
      }
    } else {
      result.push(arr[i]);
      i++;
    }
  }

  return result;
}

export const isChildAgeFilters = (filter: QuickFilter) => {
  return (
    filter.filterKey === 'AgeFilter' ||
    ['Equal to', 'Greater than or equal to', 'Less than or equal to'].includes(
      filter.label,
    )
  );
};

export const isSingleSelectionChildFilter = (filter: QuickFilter) => {
  const keys = Object.keys(PatientSearchFilters).filter(
    (k) =>
      PatientSearchFilters[k as keyof typeof PatientSearchFilters] ===
        PatientSearchFilters.SpecificHospital ||
      PatientSearchFilters[k as keyof typeof PatientSearchFilters] ===
        PatientSearchFilters.AssignedToMyTeam,
  );
  // check if the filter id starts with any of the keys
  // if it does, then it is a single selection child filter
  return keys.some((key) => filter.id.startsWith(key));
};

export const getConvertedFilters = (
  filters: string[],
  searchFiltersMap?: Partial<FilterConfigsMap>,
): string[] => {
  const result: string[] = [];

  if (!filters || filters.length === 0) return result;

  if (
    filters.length === 1 &&
    (filters[0] === 'All Patients' || filters[0] === 'All patient')
  ) {
    return result;
  }

  for (let i = 0; i < filters.length; i++) {
    const label = filters[i];
    const filterKey = getFilterKeyByLabel(label, searchFiltersMap);
    const isEnumLabel = !!filterKey;

    if (isEnumLabel) {
      const backendLabel =
        filterKey && isPatientSearchFilterKey(filterKey)
          ? getDefaultSearchFilterLabel(filterKey)
          : label;
      result.push(backendLabel);

      const type = getConfiguredSearchFilterType(
        filterKey,
        backendLabel,
        searchFiltersMap,
      );
      const next = i + 1 < filters.length ? filters[i + 1] : undefined;
      const nextIsLabel = next
        ? !!getFilterKeyByLabel(next, searchFiltersMap)
        : false;

      // Only treat the next item as the current label's value if it exists and is not a new label
      if (next && !nextIsLabel) {
        if (filterKey === 'AgeFilter') {
          const transformedValue = next
            .split(/,\s*/)
            .map(mapTextToRelationaOperator)
            .join(', ');
          result.push(transformedValue);
          i++; // consume the value
        } else if (filterKey === 'SpecificUnit') {
          const transformedValue = next
            .split(',')
            .map((item) => item.split('-')[0])
            .join(', ');
          result.push(transformedValue);
          i++; // consume the value
        } else if (type !== SearchFilterType.NO_SELECTION) {
          result.push(next);
          i++; // consume the value
        }
        // Otherwise NO_SELECTION type does not carry a value, or the next item is a new label; leave i unchanged for the next iteration
      }
    } else {
      // Non-enum label (floating value); keep as-is to avoid data loss
      result.push(label);
    }
  }

  return result.map((item) =>
    item === 'eGFR' ? '.Estimated Glomerular Filtration Rate' : item,
  ) as string[];
};

export const convertToFilterString = (
  found?: SavedSearchFilter,
  searchFiltersMap?: Partial<FilterConfigsMap>,
): string[] => {
  if (!found) return [];
  const normalizedText = found.text.trim().toLowerCase();
  if (normalizedText === 'all patients' || normalizedText === 'all patient') {
    return ['All Patients'];
  }
  const filterText = found?.text;
  const result: string[] = [];

  let currentEnum: string | null = null;
  let currentValue: string[] = [];

  filterText.split(/\s*,\s*/).forEach((part) => {
    if (getFilterKeyByLabel(part, searchFiltersMap)) {
      if (currentEnum !== null) {
        result.push(currentEnum);
        if (currentValue.length > 0) {
          result.push(currentValue.join(', '));
        }
      }
      currentEnum = part;
      currentValue = [];
    } else {
      currentValue.push(part);
    }
  });

  if (currentEnum !== null) {
    result.push(currentEnum);
    if (currentValue.length > 0) {
      result.push(currentValue.join(', '));
    }
  }

  return result;
};

export const getCurrentParentFilter = (selectedFilters: QuickFilter[]) => {
  // backward searching the selected filters
  for (let i = selectedFilters.length - 1; i >= 0; i--) {
    const filter = selectedFilters[i];
    if (filter.filterKey) {
      return filter;
    }
  }
  return undefined;
};

export const validateFiltersCombinations = (selectedFilters: QuickFilter[]) => {
  const ageIsValid = validateAge(selectedFilters);

  if (!ageIsValid) {
    showAlert([PatientSearchFilters.AgeFilter]);
    return false;
  }
  return true;
};

const showAlert = (filters: PatientSearchFilters[]) => {
  Swal.fire({
    icon: 'warning',
    title: `<div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
      <span style={{fontSize: '18px'}}>The combination of filters invalid</span>
        ${filters.map((f) => `<div style={{fontSize: '12px'}}>${f}</div>`)}
    </div>`,
  });
};
